import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Redirect, Route, Switch } from "react-router-dom";
// i18n
import i18n from "../../config/translation/i18n";
// moment
import moment from "moment";
import "moment/min/locales.min";
// Constants
import { BASE_URL, THEME_STYLE, ADMIN, CANDIDATE } from "../../config/constants";
// Ducks
import { fetchProfile } from "../../services/auth.ducks";
import { fetchTranslationForLocale } from "../../services/localization.ducks";
import { fetchAppFeatureSettings } from "../../services/clientSettings.ducks";
import { navigateTo } from "./Dashboard.ducks";
// Components
import Bookings from "./Bookings";
import ExamDetails from "./ExamDetails";
import Exams from "./Exams";
import Header from "./components/Header";
import Settings from "./Settings";
import UsersList from "./UsersList";
import Vouchers from "./Vouchers";
import ManualAssignment from "./ManualAssignment";
import ExaminationAssignment from "./ManualAssignment/ExaminationAssignment";
import PaymentHistory from "./PaymentHistory";

class Dashboard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      hasTranslations: false,
      hasActiveVoucherSystem: true,
      hasActivePayment: false
    };
  }

  componentDidMount() {
    const { auth, navigateTo } = this.props;
    const docBody = document.getElementsByTagName(`body`)[0];
    const themeStyle = localStorage.getItem(THEME_STYLE);

    if (auth.profile.isPasswordIntendedToChange) {
      navigateTo(`/one_time_password`);
    } else if (auth.profile.isProfileIntendedToApprove) {
      navigateTo(`/one_time_profile`);
    } else if (auth.profile.isOpenPayment) {
      navigateTo("/openPayment");
    }

    // set theme
    themeStyle === `Light`
      ? docBody.classList.add(`smart-style-2`)
      : docBody.classList.add(`smart-style-0`);
    // to deal with date formatting
    const locale = localStorage.getItem(`i18nextLng`);
    moment.locale(locale);

    this.getAppFeatureSettingsAsync();
  }

  getAppFeatureSettingsAsync() {
    const { fetchAppFeatureSettings } = this.props;

    fetchAppFeatureSettings().then((appFeatureSettings) => {
      this.setState({
        hasActiveVoucherSystem: appFeatureSettings.hasActiveVoucherSystem,
        hasActivePayment: appFeatureSettings.hasActivePayment
      });
    });
  }

  static getDerivedStateFromProps(props, state) {
    if (!state.hasTranslations) {
      const { language } = i18n;
      props.fetchTranslationForLocale(language);

      return {
        hasTranslations: true
      };
    }

    if (props.auth.profile.isPasswordIntendedToChange) {
      navigateTo(`/one_time_password`);
    } else if (props.auth.profile.isProfileIntendedToApprove) {
      navigateTo(`/one_time_profile`);
    } else if (props.auth.profile.isOpenPayment) {
      navigateTo("/openPayment");
    }

    return null;
  }

  returnComponentForRole(adminComponent, candidateComponent) {
    const { auth } = this.props;
    switch (auth.role) {
      case CANDIDATE:
        return candidateComponent;
      case ADMIN:
        return adminComponent;
      default:
        return null;
    }
  }

  render() {
    const {
      auth,
      users: { selectedCandidate },
      selectedSlots
    } = this.props;

    const { hasActiveVoucherSystem, hasActivePayment } = this.state;

    const isAdmin = auth.role === `admin`;
    const isCandidateSelected = Boolean(selectedCandidate);

    /**
     * This is the case the user is no longer active
     * so the only available route is /bookings
     */
    if (!auth.profile.isUserActive && !isAdmin) {
      return (
        <>
          <Header />
          <Switch>
            <Route exact path={`${BASE_URL}/dashboard/bookings`} component={Bookings} />
            <Redirect to="/dashboard/bookings" />
          </Switch>
        </>
      );
    }

    return (
      <>
        <Header />
        <Route
          exact
          path={`${BASE_URL}/dashboard/assign`}
          render={() =>
            isAdmin && isCandidateSelected ? (
              <ManualAssignment />
            ) : isAdmin && !isCandidateSelected ? (
              <Redirect to={`${BASE_URL}/dashboard/users`} />
            ) : null
          }
        />
        <Route
          exact
          path={`${BASE_URL}/dashboard/assign/exams`}
          render={() =>
            isAdmin && isCandidateSelected && selectedSlots ? (
              <ExaminationAssignment />
            ) : (
              <Redirect to={`${BASE_URL}/dashboard/assign`} />
            )
          }
        />
        <Route exact path={`${BASE_URL}/dashboard/details`} component={ExamDetails} />
        <Route
          exact
          path={`${BASE_URL}/dashboard/settings`}
          render={() => this.returnComponentForRole(null, <Settings />)}
        />
        <Route
          exact
          path={`${BASE_URL}/dashboard/bookings`}
          render={() =>
            isAdmin && isCandidateSelected ? (
              <Bookings />
            ) : isAdmin && !isCandidateSelected ? (
              <Redirect to={`${BASE_URL}/dashboard/users`} />
            ) : !isAdmin ? (
              <Bookings />
            ) : null
          }
        />
        <Route
          exact
          path={`${BASE_URL}/dashboard/paymentHistory`}
          render={() => (hasActivePayment && !isAdmin && !isCandidateSelected ? <PaymentHistory /> : null)}
        />
        <Route
          exact
          path={`${BASE_URL}/dashboard/vouchers`}
          render={() => (hasActiveVoucherSystem ? <Vouchers /> : null)}
        />
        <Route
          exact
          path={`${BASE_URL}/dashboard`}
          render={() =>
            isAdmin && isCandidateSelected ? (
              <Exams />
            ) : isAdmin && !isCandidateSelected ? (
              <Redirect to={`${BASE_URL}/dashboard/users`} />
            ) : !isAdmin ? (
              <Exams />
            ) : null
          }
        />
        <Route
          exact
          path={`${BASE_URL}/dashboard/users`}
          render={() => this.returnComponentForRole(<UsersList />, null)}
        />
      </>
    );
  }
}

Dashboard.propTypes = {
  auth: PropTypes.shape({
    profile: PropTypes.shape({
      isUserActive: PropTypes.bool,
      isPasswordIntendedToChange: PropTypes.bool,
      isProfileIntendedToApprove: PropTypes.bool,
      isOpenPayment: PropTypes.bool
    }),
    role: PropTypes.string
  }),
  fetchProfile: PropTypes.func.isRequired,
  fetchTranslationForLocale: PropTypes.func.isRequired,
  navigateTo: PropTypes.func.isRequired,
  fetchAppFeatureSettings: PropTypes.func.isRequired,
  users: PropTypes.shape({ selectedCandidate: PropTypes.object })
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  users: state.users,
  selectedSlots: state.exams.selectedSlots
});

const mapDispatchToProps = {
  fetchProfile,
  fetchTranslationForLocale,
  navigateTo,
  fetchAppFeatureSettings
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Dashboard);
